import { colLabel, normalizeTileIds } from './tileRange.js';
import { buildColorVars } from './colorVars.js';

/**
 * Expands a tile expression that may contain "#bundleId" references.
 * Each token is either a plain range ("E-Z1-9") or a bundle ref ("#eckfahne-nw").
 * Bundle refs are looked up in bundleDefs and their tile ranges expanded.
 * Returns a deduped, flat array of tile IDs (insertion order, later wins for
 * overriding category assignments).
 *
 * @param {string} expr        - Comma-separated tokens, e.g. "E-Z1-9, #eckfahne-nw"
 * @param {Object} bundleDefs  - { [bundleId]: { tiles: string } }
 * @returns {string[]}
 */
function expandBundleRefs( expr, bundleDefs ) {
	const seen   = new Set();
	const result = [];
	for ( const token of expr.split( ',' ) ) {
		const t = token.trim();
		if ( ! t ) continue;
		const raw  = t.startsWith( '#' )
			? bundleDefs[ t.slice( 1 ) ]?.tiles ?? ''
			: t;
		for ( const id of normalizeTileIds( raw ) ) {
			if ( ! seen.has( id ) ) { seen.add( id ); result.push( id ); }
		}
	}
	return result;
}

/**
 * Builds the full tile grid from a field configuration.
 *
 * Supports two formats:
 *
 *   NEW (preferred):
 *     categories: { [slug]: { label, factor, tiles? } }
 *     bundles:    { [id]:   { label, tiles } }
 *
 *   LEGACY (backward-compat):
 *     price_categories: [{ slug, label, factor }]
 *     tile_category_map: [{ category, tiles }] | { tileId: slug }
 *     tile_bundles: [{ id, label, tile_ids, factor?, price? }]
 *
 * In the new format the category assignment for each tile is determined by
 * iterating categories in insertion order — later entries override earlier ones
 * (e.g. "box" overrides the "pa" tiles that are a subset of its range).
 * Bundle prices are derived from the category of the bundle's tiles (no
 * per-bundle factor/price needed).
 *
 * @param {Object} config
 * @returns {{
 *   totalWidth, totalHeight, gridWidth, gridHeight, cols, rows,
 *   tiles: Array,
 *   normalizedBundles: Array
 * }}
 */
export function buildGrid( config ) {
	const {
		cell_width:      cellW,
		cell_height:     cellH,
		cols,
		rows,
		grid_width_pct:  gridWidthPct  = 100,
		grid_height_pct: gridHeightPct = 100,
		offset_x:        offsetX       = 0,
		offset_y:        offsetY       = 0,
		disabled_tiles:  disabledTilesRaw = '',
	} = config;

	// Guard against invalid / missing config values that would produce an infinite loop
	// or a degenerate grid (e.g. unpublished CPT with missing meta).
	const EMPTY_GRID = { totalWidth: 1, totalHeight: 1, gridWidth: 0, gridHeight: 0, cols: 0, rows: 0, tiles: [], normalizedBundles: [] };
	if (
		! Number.isFinite( cols )   || cols   <= 0 ||
		! Number.isFinite( rows )   || rows   <= 0 ||
		! Number.isFinite( cellW )  || cellW  <= 0 ||
		! Number.isFinite( cellH )  || cellH  <= 0
	) return EMPTY_GRID;

	// Actual grid dimensions in metres
	const gridW  = cols * cellW;
	const gridH  = rows * cellH;
	// SVG coordinate space (= the image area)
	const totalW = gridW / ( gridWidthPct  / 100 );
	const totalH = gridH / ( gridHeightPct / 100 );
	// Top-left of grid within the SVG, centred + optional nudge
	const startX = ( totalW - gridW ) / 2 + offsetX;
	const startY = ( totalH - gridH ) / 2 + offsetY;

	let normalizedBundles = [];
	let tileBundleMap     = {}; // tileId → bundleId
	let tileCategoryMap   = {}; // tileId → categorySlug

	// ── New format: categories{} + bundles{} ─────────────────────────────────
	// Detect new format by presence of either key — bundles may be omitted.
	const isNewFormat = (
		( config.bundles    != null && typeof config.bundles    === 'object' && ! Array.isArray( config.bundles ) )
		|| ( config.categories != null && typeof config.categories === 'object' && ! Array.isArray( config.categories ) )
	);

	if ( isNewFormat ) {
		const bundleDefs   = config.bundles ?? {};
		const categoryDefs = config.categories ?? {};

		// 1. Parse bundles → normalizedBundles array + tileBundleMap
		for ( const [ id, bundle ] of Object.entries( bundleDefs ) ) {
			const tileIds = normalizeTileIds( bundle.tiles );
			normalizedBundles.push( { id, label: bundle.label, tile_ids: tileIds } );
			for ( const tileId of tileIds ) {
				tileBundleMap[ tileId ] = id;
			}
		}

		// 2. Build tileCategoryMap in insertion order (later entries override)
		for ( const [ slug, cat ] of Object.entries( categoryDefs ) ) {
			if ( ! cat.tiles ) continue; // "default" has no tiles entry
			for ( const id of expandBundleRefs( cat.tiles, bundleDefs ) ) {
				tileCategoryMap[ id ] = slug;
			}
		}

		// 3. Annotate each bundle with its resolved category slug
		for ( const bundle of normalizedBundles ) {
			bundle.category = tileCategoryMap[ bundle.tile_ids[ 0 ] ] ?? 'default';
		}

	// ── Legacy format ─────────────────────────────────────────────────────────
	} else {
		const legacyBundles = config.tile_bundles ?? [];
		normalizedBundles   = legacyBundles.map( ( b ) => ( {
			...b,
			tile_ids: normalizeTileIds( b.tile_ids ),
		} ) );
		for ( const bundle of normalizedBundles ) {
			for ( const id of bundle.tile_ids ) {
				tileBundleMap[ id ] = bundle.id;
			}
		}

		const catMapRaw = config.tile_category_map ?? {};
		if ( Array.isArray( catMapRaw ) ) {
			for ( const entry of catMapRaw ) {
				for ( const id of normalizeTileIds( entry.tiles ) ) {
					tileCategoryMap[ id ] = entry.category;
				}
			}
		} else if ( catMapRaw && typeof catMapRaw === 'object' ) {
			Object.assign( tileCategoryMap, catMapRaw );
		}
	}

	const disabledSet = new Set( normalizeTileIds( disabledTilesRaw ) );

	// Custom per-tile-group color overrides — independent of categories.
	// Key: tile range expression (same syntax as categories.tiles), value: a
	// `colors`-shaped object where any omitted key falls back to the global
	// color via normal CSS custom property inheritance (see colorVars.js).
	const customColorDefs     = config.colors?.custom ?? {};
	const bundleDefsForCustom = ( config.bundles && typeof config.bundles === 'object' && ! Array.isArray( config.bundles ) )
		? config.bundles
		: {};
	const tileCustomStyleMap  = {}; // tileId → CSS custom property map
	for ( const [ expr, customColors ] of Object.entries( customColorDefs ) ) {
		const vars = buildColorVars( customColors );
		for ( const id of expandBundleRefs( expr, bundleDefsForCustom ) ) {
			tileCustomStyleMap[ id ] = vars;
		}
	}

	const tiles = Array.from( { length: cols * rows }, ( _, i ) => {
		const col = i % cols;
		const row = Math.floor( i / cols );
		const id  = colLabel( col ) + ( row + 1 );
		return {
			id, col, row,
			x: startX + col * cellW,
			y: startY + row * cellH,
			w: cellW,
			h: cellH,
			category:    tileCategoryMap[ id ]   ?? 'default',
			bundleId:    tileBundleMap[ id ]     ?? null,
			customStyle: tileCustomStyleMap[ id ] ?? null,
			disabled:    disabledSet.has( id ),
		};
	} );

	// Re-derive tile_ids from the actual tile assignments so that overlapping
	// bundle definitions (last one wins) don't cause mismatches later.
	// E.g. if "elfmeter-sued" (P45) overrides "sechzehn-sued-rechts" (M–Z42–47)
	// for tile P45, then sechzehn-sued-rechts.tile_ids should only have 83 tiles.
	for ( const bundle of normalizedBundles ) {
		bundle.tile_ids = tiles
			.filter( ( t ) => t.bundleId === bundle.id )
			.map( ( t ) => t.id );
	}

	return {
		totalWidth:  totalW,
		totalHeight: totalH,
		gridWidth:   gridW,
		gridHeight:  gridH,
		cols,
		rows,
		tiles,
		normalizedBundles,
	};
}
